import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ShieldCheck,
  HardHat,
  FileCheck2,
  ClipboardList,
  CircuitBoard,
  Building2,
} from "lucide-react";
import { SERVICES, PROJECTS, STATS, SITE } from "../lib/site";
import SectionLabel from "../components/SectionLabel";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative bg-navy text-white overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1210043/pexels-photo-1210043.jpeg"
            alt="Subway platform infrastructure"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/40" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:80px_80px]" />
        </div>

        <div className="relative container-pro pt-24 pb-24 md:pt-40 md:pb-44">
          <div className="flex items-center gap-3 mb-8">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slateblue-400">
              EST. 2016 · NYC
            </span>
            <span className="h-px w-16 bg-slateblue-400/60" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slateblue-400">
              CONTRACT TYPE · WBE
            </span>
          </div>

          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[1.02] max-w-5xl animate-fade-up">
            Electrical engineering
            <br />
            for the systems that
            <br />
            <span className="text-slateblue-400">move New York.</span>
          </h1>

          <p className="mt-8 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed">
            Specialty drafting, as-built documentation, and FAT/SAT
            commissioning services purpose-built for NYCT MTA transit
            infrastructure projects.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="btn-primary !bg-white !text-navy !border-white hover:!bg-slateblue-400 hover:!text-white hover:!border-slateblue-400"
            >
              Our Services
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/portfolio"
              data-testid="hero-cta-portfolio"
              className="btn-ghost !text-white !border-white/60 hover:!bg-white hover:!text-navy"
            >
              View Portfolio
            </Link>
          </div>
        </div>

        {/* credibility bar */}
        <div className="relative border-t border-white/10 bg-navy-800/40 backdrop-blur-sm">
          <div className="container-pro grid grid-cols-2 md:grid-cols-4">
            {STATS.map((s, i) => (
              <div
                key={s.label}
                data-testid={`stat-${i}`}
                className={`py-8 md:py-10 ${
                  i !== 0 ? "md:border-l border-white/10" : ""
                } ${i % 2 === 1 ? "border-l border-white/10 md:border-l" : ""}`}
              >
                <div className="font-heading font-black text-3xl md:text-4xl text-white">
                  {s.value}
                </div>
                <div className="mt-2 text-[11px] font-mono uppercase tracking-[0.2em] text-slateblue-400">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITY OVERVIEW */}
      <section className="section-pad bg-white">
        <div className="container-pro">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
            <div className="lg:col-span-7">
              <SectionLabel
                code="01 / CAPABILITIES"
                kicker="What we deliver"
                title="Engineering deliverables, prepared to MTA specification."
              />
            </div>
            <div className="lg:col-span-5">
              <p className="text-base text-slate-600 leading-relaxed">
                Every drawing, document and test procedure we produce is
                traceable, revision-controlled, and ready for submission to
                NYCT QA. We work as an extension of your engineering team —
                quietly, precisely, and on the contract calendar.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 grid-overlap">
            {SERVICES.map((s, i) => {
              const Icon = [CircuitBoard, ClipboardList, FileCheck2][i];
              return (
                <Link
                  key={s.id}
                  to="/services"
                  data-testid={`service-card-${s.id}`}
                  className="group block p-8 md:p-10 bg-white hover:bg-surface-alt transition-colors"
                >
                  <div className="flex items-center justify-between mb-10">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slateblue">
                      {s.code}
                    </span>
                    <Icon className="h-5 w-5 text-navy" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading font-bold text-xl md:text-2xl text-navy leading-snug">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                    {s.short}
                  </p>
                  <div className="mt-8 inline-flex items-center gap-2 font-heading text-[11px] uppercase tracking-[0.18em] text-navy group-hover:gap-3 transition-all">
                    Read more
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US — split */}
      <section className="bg-surface-alt section-pad border-y border-hairline">
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <SectionLabel
              code="02 / APPROACH"
              kicker="Engineering discipline"
              title="Built for inspection. Documented for closeout."
            />
            <p className="mt-8 text-base text-slate-600 leading-relaxed max-w-md">
              Transit infrastructure work doesn’t forgive shortcuts. Our
              workflow is built around the realities of MTA submittal cycles,
              QA holds, and witness testing — so your project closes out
              cleanly, the first time.
            </p>
            <Link
              to="/contact"
              data-testid="approach-cta"
              className="mt-8 link-underline"
            >
              Start a conversation
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 grid-overlap">
            {[
              {
                icon: ShieldCheck,
                title: "MTA-Compliant Standards",
                body: "Drawings prepared to C-Standards, EE-Series, and Division-specific procedures.",
              },
              {
                icon: HardHat,
                title: "Field-Verified Accuracy",
                body: "As-built packages built from physical site walks — not assumptions.",
              },
              {
                icon: FileCheck2,
                title: "Traceable Documentation",
                body: "Every test step linked to a requirement, a revision, and a witness signature.",
              },
              {
                icon: Building2,
                title: "Prime & Sub Friendly",
                body: "We integrate cleanly with GCs, EORs, and integrators on multi-discipline jobs.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-white p-7 md:p-8">
                <Icon className="h-5 w-5 text-slateblue" strokeWidth={1.5} />
                <h4 className="mt-6 font-heading font-bold text-navy text-base">
                  {title}
                </h4>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="section-pad bg-white">
        <div className="container-pro">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <SectionLabel
              code="03 / PORTFOLIO"
              kicker="Selected work"
              title="Recent NYCT MTA contracts."
            />
            <Link
              to="/portfolio"
              data-testid="featured-portfolio-cta"
              className="link-underline"
            >
              All projects
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {PROJECTS.slice(0, 2).map((p) => (
              <article
                key={p.id}
                data-testid={`featured-project-${p.id}`}
                className={`group relative ${p.span} bg-navy text-white overflow-hidden border border-hairline-dark`}
              >
                <div className="relative h-72 md:h-96 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-75 group-hover:scale-[1.02] transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
                </div>
                <div className="absolute inset-0 p-6 md:p-10 flex flex-col justify-end">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slateblue-400">
                      {p.code}
                    </span>
                    <span className="h-px w-8 bg-slateblue-400/60" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slateblue-400">
                      {p.discipline}
                    </span>
                  </div>
                  <h3 className="font-heading font-bold text-xl md:text-2xl max-w-md leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-white/75 max-w-md">
                    {p.summary}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT STRIP */}
      <section
        data-testid="cta-strip"
        className="bg-navy text-white border-t border-navy-800"
      >
        <div className="container-pro py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <span className="overline !text-slateblue-400">Next contract</span>
            <h3 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight max-w-3xl">
              Have a transit infrastructure scope on deck? Let’s talk
              specifications.
            </h3>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/contact"
              data-testid="bottom-cta-contact"
              className="btn-primary !bg-white !text-navy !border-white hover:!bg-slateblue-400 hover:!text-white hover:!border-slateblue-400"
            >
              Request a Quote
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
